import { useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { Button } from 'flowbite-react';
import { CheckIcon, XIcon } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';

import { useApi } from '../../../api/useApi.hook';
import { clearPackageSelections } from '../../../lib/creditSelections.utils';
import {
    clearReturnPath as clearCreditTopUpReturnPath,
    getReturnPath as getCreditTopUpReturnPath
} from '../../../lib/creditTopUpReturn.utils';
import { pathWithParams } from '../../../lib/routing.utils';
import { AppHeader } from '../../components/appHeader/appHeader.component';
import { RouteIndex } from '../../routeIndex';

const CreditPaymentReturnView = () => {
    const api = useApi();
    const queryClient = useQueryClient();
    const nav = useNavigate();

    const { orderId } = useParams<{ orderId: string }>();

    // Capture the origin path once on mount so we don't lose it after the user
    // clicks the success/back button (which clears the entry).
    const [returnPath] = useState<string | null>(() => getCreditTopUpReturnPath());

    const paymentStatus = useQuery({
        queryKey: ['creditPurchaseOrderStatus', orderId],
        queryFn: async () => {
            return api.v1.credits.getPurchaseOrderStatus(orderId ?? '');
        },
        refetchInterval: (query) => {
            // Poll every 3 seconds while status is PENDING or CREATED (GoPay)
            const status = query.state.data?.status;
            if (status === 'PENDING' || status === 'CREATED') return 3000;
            return false;
        },
    });

    const handleGoToOriginOrWallet = useCallback(async () => {
        await queryClient.invalidateQueries({ queryKey: ['creditBalance'] });
        clearCreditTopUpReturnPath();
        nav(returnPath ?? pathWithParams(RouteIndex.wallet, {}));
    }, [nav, queryClient, returnPath]);

    const handleGoBack = useCallback(() => {
        nav(pathWithParams(RouteIndex.topUpCredit, {}));
    }, [nav]);

    const status = paymentStatus.data?.status;
    const isPaid = status === 'CAPTURED';
    const isFailed = status === 'UNPAID' || status === 'FAILED';

    useEffect(() => {
        if (isPaid) {
            clearPackageSelections();
        }
    }, [isPaid]);

    return (
        <>
            <AppHeader
                pageTitle='Výsledek platby'
                backLink={returnPath ?? RouteIndex.wallet}
            />

            <div className="flex flex-col justify-start items-center min-h-screen pt-14">
                <div className='flex flex-col items-center justify-center gap-6 p-4 md:p-8 lg:py-12 w-[36rem] max-w-full min-h-[calc(100vh-5rem)]'>

                    {/* Loading / Pending */}
                    {(!status || status === 'PENDING' || status === 'CREATED') && (
                        <div className={clsx(
                            'relative justify-center w-[8rem] aspect-square rounded-full text-2xl font-bold border border-gray-200'
                        )}>
                            <div className='absolute left-1 top-1 right-1 bottom-1 rounded-full [background:conic-gradient(#3498db_0deg_270deg,transparent_270deg_360deg)] [animation:spin_3s_linear_infinite]' />
                            <div className='absolute left-2 top-2 right-2 bottom-2 rounded-full flex flex-row items-center justify-center bg-gray-100 text-gray-700'></div>
                        </div>
                    )}

                    {/* Success */}
                    {isPaid && (
                        <div className='flex flex-col items-center gap-6'>
                            <div className={clsx(
                                'relative justify-center w-[8rem] aspect-square rounded-full text-2xl font-bold border-[3px] border-lime-600',
                                'flex flex-row items-center justify-center text-lime-600 pt-2'
                            )}>
                                <CheckIcon size={64} />
                            </div>
                            <p className='text-xl font-semibold text-lime-700'>
                                Platba byla přijata. Přidáno {paymentStatus.data?.totalCredits} kreditů.
                            </p>
                            <Button
                                color="primary"
                                size='lg'
                                onClick={handleGoToOriginOrWallet}
                            >{returnPath ? 'Pokračovat' : 'Přejít do peněženky'}</Button>
                        </div>
                    )}

                    {/* Failed */}
                    {isFailed && (
                        <div className='flex flex-col items-center gap-6'>
                            <div className={clsx(
                                'relative justify-center w-[8rem] aspect-square rounded-full text-2xl font-bold border-[3px] border-red-600',
                                'flex flex-row items-center justify-center text-red-600 pt-2'
                            )}>
                                <XIcon size={64} />
                            </div>
                            <p className='text-xl font-semibold text-gray-700'>Platba se nezdařila.</p>
                            {paymentStatus.data?.resultText && (
                                <p className='text-sm text-gray-500'>{paymentStatus.data.resultText}</p>
                            )}
                            <Button
                                color="primary"
                                size='lg'
                                onClick={handleGoBack}
                            >Zkusit znovu</Button>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default CreditPaymentReturnView;
